import { ChevronDown, ChevronUp, Maximize2, Plus, RotateCcw, Save, SlidersHorizontal, Trash2 } from "lucide-react";
import type { DragEvent, ReactNode } from "react";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { deleteAsset, deletePlan, getAssets, getChartIntelligence, getChartSettings, getMonitoringSummary, getPlans, refreshMonitoring, saveAsset, saveChartSettings, savePlan } from "../../data/api";
import { allocationPaletteSwatches, defaultAllocationLabelColor } from "../../data/allocationColors";
import { defaultChartSettings, mergeChartSettings } from "../../data/chartSettings";
import { allocationAssets as fallbackAllocationAssets, instruments as fallbackInstruments, ladderBudgets } from "../../data/mockData";
import { buildLadder, money, number, pct, weightedAverageIndex } from "../../utils/calculations";
import type { AllocationAsset, AssetRecord, ChartIntelligencePayload, ChartLineStyleConfig, ChartSettings, ChartStyleKey, Instrument, RulePlanBundle } from "../../types";

const assetColors = ["#2267b8", "#6d3aa6", "#168760", "#a87818", "#596a7c", "#9d3444", "#276f8c", "#27896f"];
const loadPriceIntelligenceChart = () => import("../../components/EChartsPriceChart");
const PriceIntelligenceChart = lazy(() => loadPriceIntelligenceChart().then((module) => ({ default: module.PriceIntelligenceChart })));

type BudgetCurrency = "USD" | "KRW";
type MovingAverageKey = "ma20" | "ma50" | "ma200" | "custom";

interface StepAllocationItem {
  id: string;
  stepId: string;
  assetTicker: string;
  assetName: string;
  labelColor?: string;
  amount: number;
}

interface AllocationEditorState {
  mode: "add" | "edit";
  stepId: string;
  itemId?: string;
  assetTicker: string;
  assetName: string;
  labelColor: string;
  amountText: string;
}

interface StepBudgetOverride {
  basis: "amount" | "percent";
  value: number;
}

interface StepBudgetDraft {
  field: "amount" | "percent";
  text: string;
}

const paletteItems: Array<{ key: ChartStyleKey; label: string }> = [
  { key: "priorHigh", label: "Prior High" },
  { key: "trigger", label: "Trigger" },
  { key: "ladder", label: "CP Ladder" },
  { key: "ma20", label: "MA 20" },
  { key: "ma50", label: "MA 50" },
  { key: "ma200", label: "MA 200" },
  { key: "customMa", label: "Custom MA" },
  { key: "futuresBasis", label: "Futures Basis" }
];

const chartPaletteSwatches = [
  "#ff5d6e", "#ff8d98", "#ffc857", "#ffe08a", "#24d18f", "#70f0c2",
  "#42a5ff", "#0f73ff", "#8bd3ff", "#9b4de3", "#c77dff", "#f472b6",
  "#ffffff", "#cdd9e7", "#91a3b8", "#667a91", "#334155", "#05080d",
  "#ef4444", "#f97316", "#eab308", "#22c55e", "#06b6d4", "#3b82f6",
  "#6366f1", "#a855f7", "#ec4899", "#14b8a6", "#84cc16", "#facc15"
] as const;

function chartSettingsSignature(settings: ChartSettings) {
  const { updatedAt: _updatedAt, ...rest } = settings;
  return JSON.stringify(rest);
}

const emptyAssetDraft: AssetRecord = {
  ticker: "",
  name: "",
  assetClass: "ETF",
  market: "US",
  currency: "USD",
  defaultWeightPct: 0,
  active: true
};

const fallbackAssetRecords: AssetRecord[] = fallbackAllocationAssets.map((asset) => ({
  ticker: asset.ticker,
  name: asset.name,
  assetClass: asset.ticker.toLowerCase().includes("cash") ? "Cash" : "ETF",
  market: asset.ticker.includes("KODEX") ? "KR" : asset.ticker.toLowerCase().includes("cash") ? "Global" : "US",
  currency: asset.ticker.includes("KODEX") ? "KRW" : "USD",
  defaultWeightPct: asset.weightPct,
  active: true
}));

function mergeInstrumentUniverse(remoteInstruments: Instrument[]) {
  const byId = new Map(fallbackInstruments.map((instrument) => [instrument.id, instrument]));
  for (const instrument of remoteInstruments) {
    if (byId.has(instrument.id)) byId.set(instrument.id, instrument);
  }
  return Array.from(byId.values());
}

function cleanNumber(value: string, fallback: number, options: { min?: number; max?: number; integer?: boolean } = {}) {
  if (value.trim() === "") return fallback;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  const rounded = options.integer ? Math.round(parsed) : parsed;
  return Math.min(options.max ?? rounded, Math.max(options.min ?? rounded, rounded));
}

function editableNumber(value: string, options: { min?: number; max?: number; integer?: boolean } = {}) {
  if (value.trim() === "") return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const rounded = options.integer ? Math.round(parsed) : parsed;
  return Math.min(options.max ?? rounded, Math.max(options.min ?? rounded, rounded));
}

function numberText(value: number) {
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(4)));
}

function budgetInputText(value: number) {
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, "");
}

function percentInputText(value: number) {
  return value.toFixed(1);
}

function uniquePlanId(direction: "CP" | "RCP", instrumentId: string) {
  const stamp = new Date().toISOString().replace(/[-:TZ.]/g, "").slice(0, 12);
  return `${direction.toLowerCase()}-${instrumentId}-${stamp}`;
}

function budgetWeights(stepCount: number) {
  const count = Math.max(2, Math.min(201, Math.round(stepCount + 1)));
  if (count <= ladderBudgets.length) return ladderBudgets.slice(0, count);
  return Array.from({ length: count }, () => 1);
}

function ladderStepId(index: number) {
  return index === 0 ? "trigger" : `step-${index}`;
}

function baseStepId(stepId: string, planId?: string) {
  if (planId && stepId.startsWith(`${planId}-`)) return stepId.slice(planId.length + 1);
  return stepId;
}

function allocationItemId(stepId: string, ticker: string) {
  return `${stepId}-${ticker}-${Date.now()}-${Math.floor(Math.random() * 10000)}`.replace(/[^a-zA-Z0-9_-]/g, "-");
}

function stableAllocationId(planId: string, stepId: string, itemId: string) {
  if (itemId.startsWith(`${planId}-`)) return itemId;
  return `${planId}-${stepId}-${itemId}`.replace(/[^a-zA-Z0-9_-]/g, "-");
}

function allocationItemsFromPlan(plan?: RulePlanBundle) {
  if (!plan) return {};
  return plan.allocations.reduce((acc, allocation) => {
    const stepId = baseStepId(allocation.stepId, plan.id);
    const item: StepAllocationItem = {
      id: allocation.id ?? allocationItemId(stepId, allocation.assetTicker),
      stepId,
      assetTicker: allocation.assetTicker,
      assetName: allocation.assetName,
      labelColor: allocation.labelColor,
      amount: allocation.plannedAmount
    };
    acc[stepId] = [...(acc[stepId] ?? []), item];
    return acc;
  }, {} as Record<string, StepAllocationItem[]>);
}

function defaultStepAllocations(steps: Array<{ id: string; budget: number }>) {
  return steps.reduce((acc, step) => {
    acc[step.id] = [];
    return acc;
  }, {} as Record<string, StepAllocationItem[]>);
}

function parseChartDate(value: string) {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function chartRangeToMonths(range: ChartSettings["chart"]["range"]) {
  if (range === "3M") return 3;
  if (range === "6M") return 6;
  if (range === "1Y") return 12;
  if (range === "3Y") return 36;
  return 120;
}

function visibleHistoryForChartRange(history: ChartIntelligencePayload["history"], range: ChartSettings["chart"]["range"]) {
  if (!history.length) return [];
  const latestDate = parseChartDate(history[history.length - 1].date);
  if (!latestDate) return history;
  const cutoff = new Date(latestDate);
  cutoff.setMonth(cutoff.getMonth() - chartRangeToMonths(range));
  const ranged = history.filter((bar) => {
    const date = parseChartDate(bar.date);
    return date ? date >= cutoff : false;
  });
  return ranged.length ? ranged : history;
}

interface DashboardProps {
  workspace?: "dashboard" | "plans";
}

export default function Dashboard({ workspace = "dashboard" }: DashboardProps) {
  const isPlansWorkspace = workspace === "plans";
  const [direction, setDirection] = useState<"CP" | "RCP">("CP");
  const [spacingBasis, setSpacingBasis] = useState<"prior-high" | "previous-ladder">("prior-high");
  const [referenceMode, setReferenceMode] = useState<"intraday-high" | "close-high">("intraday-high");
  const [referenceHigh, setReferenceHigh] = useState(5350);
  const [triggerDropPct, setTriggerDropPct] = useState(5);
  const [ladderCount, setLadderCount] = useState(5);
  const [spacingPctPoint, setSpacingPctPoint] = useState(2);
  const [totalBudgetInput, setTotalBudgetInput] = useState(100000);
  const [referenceHighText, setReferenceHighText] = useState("5350");
  const [triggerDropPctText, setTriggerDropPctText] = useState("5");
  const [ladderCountText, setLadderCountText] = useState("5");
  const [spacingPctPointText, setSpacingPctPointText] = useState("2");
  const [totalBudgetText, setTotalBudgetText] = useState("100000");
  const [budgetMode, setBudgetMode] = useState<"amount" | "percent">("amount");
  const [budgetCurrency, setBudgetCurrency] = useState<BudgetCurrency>("USD");
  const [stepBudgetOverrides, setStepBudgetOverrides] = useState<Record<string, StepBudgetOverride>>({});
  const [stepBudgetDrafts, setStepBudgetDrafts] = useState<Record<string, StepBudgetDraft>>({});
  const [chartSettings, setChartSettings] = useState<ChartSettings>(() => defaultChartSettings);
  const [customMaInput, setCustomMaInput] = useState(numberText(defaultChartSettings.movingAverages.customPeriod));
  const [chartSettingsLoaded, setChartSettingsLoaded] = useState(false);
  const [chartSettingsStatus, setChartSettingsStatus] = useState("Loading chart settings...");
  const [chartSettingsOpen, setChartSettingsOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [saveStatus, setSaveStatus] = useState("Draft");
  const [fullscreen, setFullscreen] = useState<string | null>(null);
  const [selectedInstrumentId, setSelectedInstrumentId] = useState("spx");
  const [plans, setPlans] = useState<RulePlanBundle[]>([]);
  const [plansLoaded, setPlansLoaded] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [draftPlanId, setDraftPlanId] = useState(() => uniquePlanId("CP", "spx"));
  const [planNameInput, setPlanNameInput] = useState("");
  const [referenceTouched, setReferenceTouched] = useState(false);
  const [universe, setUniverse] = useState<Instrument[]>(fallbackInstruments);
  const [chartData, setChartData] = useState<ChartIntelligencePayload | null>(null);
  const [assetUniverse, setAssetUniverse] = useState<AssetRecord[]>(fallbackAssetRecords);
  const [assetDraft, setAssetDraft] = useState<AssetRecord>(fallbackAssetRecords[0] ?? emptyAssetDraft);
  const [assetWeightInputs, setAssetWeightInputs] = useState<Record<string, string>>(() =>
    Object.fromEntries(fallbackAssetRecords.map((asset) => [asset.ticker, numberText(asset.defaultWeightPct)]))
  );
  const [stepAllocations, setStepAllocations] = useState<Record<string, StepAllocationItem[]>>({});
  const [allocationEditor, setAllocationEditor] = useState<AllocationEditorState | null>(null);
  const [allocationDragOverStep, setAllocationDragOverStep] = useState<string | null>(null);
  const [assetStatus, setAssetStatus] = useState("Assets ready");
  const [dataStatus, setDataStatus] = useState("Loading cached market data...");
  const planNameInputRef = useRef<HTMLInputElement | null>(null);
  const latestChartSettingsRef = useRef<ChartSettings>(defaultChartSettings);
  const chartSaveInFlightRef = useRef(false);
  const chartSaveQueuedRef = useRef(false);
  const lastSavedChartSettingsRef = useRef(chartSettingsSignature(defaultChartSettings));
  const latestPlanForSaveRef = useRef<RulePlanBundle | null>(null);
  const planSaveInFlightRef = useRef<Promise<boolean> | null>(null);
  const planSaveQueuedRef = useRef(false);
  const planManualSavePendingRef = useRef(false);
  const lastSavedPlanRef = useRef("");
  const skipNextPlanAutosaveRef = useRef(true);
  const refreshInFlightRef = useRef(false);

  const applyPlanToForm = (plan: RulePlanBundle) => {
    setSelectedPlanId(plan.id);
    setPlanNameInput(plan.name);
    setDirection(plan.direction);
    setSelectedInstrumentId(plan.instrumentId);
    setReferenceMode(plan.referenceMode);
    setReferenceHigh(plan.referenceHigh);
    setTriggerDropPct(plan.triggerPct);
    setLadderCount(plan.ladderCount);
    setSpacingPctPoint(plan.spacingPctPoint);
    setReferenceHighText(numberText(plan.referenceHigh));
    setTriggerDropPctText(numberText(plan.triggerPct));
    setLadderCountText(numberText(plan.ladderCount));
    setSpacingPctPointText(numberText(plan.spacingPctPoint));
    setSpacingBasis(plan.spacingBasis);
    setBudgetMode(plan.budgetMode);
    setBudgetCurrency(plan.budgetCurrency ?? "USD");
    setTotalBudgetInput(plan.totalBudget);
    setTotalBudgetText(numberText(plan.totalBudget));
    setStepBudgetOverrides(Object.fromEntries(plan.steps.map((step) => [
      baseStepId(step.id, plan.id),
      { basis: "amount", value: Math.max(0, step.budget) } satisfies StepBudgetOverride
    ])));
    setStepBudgetDrafts({});
    setStepAllocations(allocationItemsFromPlan(plan));
    setAllocationEditor(null);
    setSaveStatus(plan.status === "active" ? "Active / Loaded" : "Draft / Loaded");
    setReferenceTouched(true);
    skipNextPlanAutosaveRef.current = true;
  };

  useEffect(() => {
    let mounted = true;
    getMonitoringSummary().then((metrics) => {
      if (!mounted) return;
      if (metrics.length) setUniverse(mergeInstrumentUniverse(metrics.map((metric) => metric.instrument)));
    }).catch(() => {
      if (mounted) setDataStatus("Using local fallback data");
    });

    getAssets().then((assets) => {
      if (!mounted || !assets.length) return;
      setAssetUniverse(assets);
      setAssetWeightInputs(Object.fromEntries(assets.map((asset) => [asset.ticker, numberText(asset.defaultWeightPct)])));
      setAssetDraft(assets[0] ?? emptyAssetDraft);
    }).catch(() => {
      if (mounted) setAssetStatus("Using fallback assets");
    });

    getPlans().then((savedPlans) => {
      if (!mounted) return;
      setPlans(savedPlans);
      if (savedPlans[0]) {
        applyPlanToForm(savedPlans[0]);
      } else {
        setSaveStatus("No server plans found");
      }
      setPlansLoaded(true);
    }).catch(() => {
      if (!mounted) return;
      setPlansLoaded(true);
      setSaveStatus("Server plan load failed");
    });

    getChartSettings().then((settings) => {
      if (!mounted) return;
      const merged = mergeChartSettings(settings);
      latestChartSettingsRef.current = merged;
      lastSavedChartSettingsRef.current = chartSettingsSignature(merged);
      setCustomMaInput(numberText(merged.movingAverages.customPeriod));
      setChartSettings(merged);
      setChartSettingsLoaded(true);
      setChartSettingsStatus("Chart settings loaded");
    }).catch(() => {
      if (!mounted) return;
      setChartSettingsLoaded(true);
      setChartSettingsStatus("Local chart settings");
    });

    return () => {
      mounted = false;
    };
  }, []);

  const saveChartSettingsNow = async (mode: "auto" | "manual" = "auto") => {
    const settingsToSave = latestChartSettingsRef.current;
    const serialized = chartSettingsSignature(settingsToSave);
    if (serialized === lastSavedChartSettingsRef.current && mode === "auto") return true;

    if (chartSaveInFlightRef.current) {
      chartSaveQueuedRef.current = true;
      if (mode === "manual") {
        while (chartSaveInFlightRef.current) {
          await new Promise((resolve) => window.setTimeout(resolve, 80));
        }
        return saveChartSettingsNow("manual");
      }
      return false;
    }

    chartSaveInFlightRef.current = true;
    setChartSettingsStatus(mode === "manual" ? "Saving chart settings..." : "Autosaving...");
    let savedSuccessfully = false;
    try {
      const saved = mergeChartSettings(await saveChartSettings(settingsToSave));
      lastSavedChartSettingsRef.current = chartSettingsSignature(saved);
      setChartSettingsStatus(`${mode === "manual" ? "Saved" : "Autosaved"} ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`);
      savedSuccessfully = true;
      return true;
    } catch (error) {
      setChartSettingsStatus(error instanceof Error ? error.message : "Save failed");
      return false;
    } finally {
      chartSaveInFlightRef.current = false;
      const latestSerialized = chartSettingsSignature(latestChartSettingsRef.current);
      if (savedSuccessfully && (chartSaveQueuedRef.current || latestSerialized !== lastSavedChartSettingsRef.current)) {
        chartSaveQueuedRef.current = false;
        void saveChartSettingsNow("auto");
      } else if (!savedSuccessfully) {
        chartSaveQueuedRef.current = false;
      }
    }
  };

  useEffect(() => {
    latestChartSettingsRef.current = chartSettings;
    if (!chartSettingsLoaded) return;
    const serialized = chartSettingsSignature(chartSettings);
    if (serialized === lastSavedChartSettingsRef.current) return;

    setChartSettingsStatus("Autosave queued...");
    const timer = window.setTimeout(() => {
      void saveChartSettingsNow("auto");
    }, 650);

    return () => window.clearTimeout(timer);
  }, [chartSettings, chartSettingsLoaded]);

  useEffect(() => {
    if (isPlansWorkspace) return;
    const timer = window.setTimeout(() => {
      void loadPriceIntelligenceChart();
    }, 120);
    return () => window.clearTimeout(timer);
  }, [isPlansWorkspace]);

  useEffect(() => {
    let mounted = true;
    setDataStatus("Loading chart intelligence...");
    getChartIntelligence(selectedInstrumentId, chartSettings.chart.range).then((payload) => {
      if (!mounted) return;
      setChartData(payload);
      const latestBar = payload.history.at(-1);
      const source = latestBar?.source ?? payload.quote?.source ?? "No verified OHLC";
      const freshness = payload.quote?.freshness ?? (payload.history.length ? "cached" : "offline");
      setDataStatus(`${source} / ${freshness}`);
    }).catch(() => {
      if (mounted) setDataStatus("Verified OHLC unavailable");
    });
    return () => {
      mounted = false;
    };
  }, [chartSettings.chart.range, selectedInstrumentId]);

  useEffect(() => {
    const refreshDashboardData = () => {
      if (refreshInFlightRef.current) return;
      refreshInFlightRef.current = true;
      window.dispatchEvent(new CustomEvent("cp-dashboard:refresh-started"));

      void (async () => {
        try {
          setDataStatus("Requesting latest market data...");
          const refreshResult = await refreshMonitoring(selectedInstrumentId);
          if (refreshResult.enqueued) {
            setDataStatus("Refresh queued; waiting for provider response...");
            await new Promise((resolve) => window.setTimeout(resolve, 1600));
          }

          const [payload, metrics] = await Promise.all([
            getChartIntelligence(selectedInstrumentId, chartSettings.chart.range),
            getMonitoringSummary()
          ]);
          setChartData(payload);
          if (metrics.length) setUniverse(mergeInstrumentUniverse(metrics.map((metric) => metric.instrument)));

          const latestBar = payload.history.at(-1);
          const source = latestBar?.source ?? payload.quote?.source ?? "No verified OHLC";
          const freshness = payload.quote?.freshness ?? (payload.history.length ? "cached" : "offline");
          setDataStatus(`${source} / ${freshness}`);
          window.dispatchEvent(new CustomEvent("cp-dashboard:refresh-done"));
        } catch (error) {
          setDataStatus(error instanceof Error ? error.message : "Data refresh failed");
          window.dispatchEvent(new CustomEvent("cp-dashboard:refresh-failed"));
        } finally {
          refreshInFlightRef.current = false;
        }
      })();
    };

    window.addEventListener("cp-dashboard:refresh-data", refreshDashboardData);
    return () => window.removeEventListener("cp-dashboard:refresh-data", refreshDashboardData);
  }, [chartSettings.chart.range, selectedInstrumentId]);

  const selectedInstrument = chartData?.instrument ?? universe.find((instrument) => instrument.id === selectedInstrumentId) ?? fallbackInstruments[0];
  const instrumentOptions = universe.filter((instrument) => instrument.planEligible);
  const referenceCandidate = useMemo(() => {
    if (chartData?.priorHigh) {
      return referenceMode === "intraday-high"
        ? { date: chartData.priorHigh.intradayHighDate, value: chartData.priorHigh.intradayHigh }
        : { date: chartData.priorHigh.closeHighDate, value: chartData.priorHigh.closeHigh };
    }
    const history = chartData?.history ?? [];
    if (!history.length) return null;
    const values = history.map((bar) => ({
      date: bar.date,
      value: referenceMode === "intraday-high" ? bar.high ?? bar.close : bar.close
    }));
    return values.reduce((best, item) => (item.value > best.value ? item : best), values[0]);
  }, [chartData?.history, chartData?.priorHigh, referenceMode]);

  useEffect(() => {
    if (referenceCandidate?.value && !referenceTouched) {
      const nextReferenceHigh = Math.round(referenceCandidate.value * 100) / 100;
      setReferenceHigh(nextReferenceHigh);
      setReferenceHighText(numberText(nextReferenceHigh));
    }
  }, [referenceCandidate?.value, referenceTouched]);

  const allocationAssets: AllocationAsset[] = useMemo(() => {
    if (!assetUniverse.length) return fallbackAllocationAssets;
    return assetUniverse.map((asset, index) => ({
      ticker: asset.ticker,
      name: asset.name,
      weightPct: asset.defaultWeightPct,
      color: assetColors[index % assetColors.length]
    }));
  }, [assetUniverse]);

  const scaledBudgets = useMemo(() => {
    const weights = budgetWeights(ladderCount);
    const baseTotal = weights.reduce((sum, value) => sum + value, 0);
    const budgets = weights.map((value) => Math.round((value / baseTotal) * totalBudgetInput));
    const roundingDiff = totalBudgetInput - budgets.reduce((sum, value) => sum + value, 0);
    budgets[budgets.length - 1] += roundingDiff;
    return budgets;
  }, [ladderCount, totalBudgetInput]);

  const resolvedBudgets = useMemo(
    () => scaledBudgets.map((automaticBudget, index) => {
      const override = stepBudgetOverrides[ladderStepId(index)];
      if (!override) return automaticBudget;
      return override.basis === "percent" ? (totalBudgetInput * override.value) / 100 : override.value;
    }),
    [scaledBudgets, stepBudgetOverrides, totalBudgetInput]
  );

  const steps = useMemo(
    () =>
      buildLadder({
        referenceHigh,
        triggerDropPct,
        ladderCount,
        spacingPctPoint,
        spacingBasis,
        direction,
        budgets: resolvedBudgets
      }),
    [direction, ladderCount, referenceHigh, resolvedBudgets, spacingBasis, spacingPctPoint, triggerDropPct]
  );
  const allocationPanelExpanded = fullscreen === "allocation";

  useEffect(() => {
    if (!plansLoaded) return;
    setStepAllocations((current) => {
      const validStepIds = new Set(steps.map((step) => step.id));
      const next = { ...current };
      let changed = false;

      for (const step of steps) {
        if (Object.prototype.hasOwnProperty.call(next, step.id)) continue;
        next[step.id] = defaultStepAllocations([step])[step.id] ?? [];
        changed = true;
      }

      for (const stepId of Object.keys(next)) {
        if (!validStepIds.has(stepId)) {
          delete next[stepId];
          changed = true;
        }
      }

      return changed ? next : current;
    });
  }, [plansLoaded, steps]);

  const plannedWai = weightedAverageIndex(steps.map((step) => ({ level: step.indexLevel, amount: step.budget })));
  const plannedWaiReferencePct = referenceHigh > 0 ? (plannedWai / referenceHigh - 1) * 100 : 0;
  const totalBudget = totalBudgetInput;
  const ladderBudgetTotal = steps.reduce((sum, step) => sum + step.budget, 0);
  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId);
  const currentPlanId = selectedPlanId || draftPlanId;
  const planName = planNameInput.trim();
  const allocatedBudget = ladderBudgetTotal;
  const allocationWeightTotal = totalBudget > 0 ? (allocatedBudget / totalBudget) * 100 : 0;
  const remainingBudget = totalBudget - allocatedBudget;
  const remainingWeightTotal = totalBudget > 0 ? (remainingBudget / totalBudget) * 100 : 0;
  const hasVerifiedChartData = (chartData?.history.length ?? 0) > 0;
  const latestChartBar = chartData?.history.at(-1);
  const chartDistanceStats = useMemo(() => {
    const history = chartData?.history ?? [];
    const latestClose = latestChartBar?.close;
    if (!history.length || typeof latestClose !== "number" || !Number.isFinite(latestClose)) return null;
    const visibleHistory = visibleHistoryForChartRange(history, chartSettings.chart.range);
    const rangeLow = visibleHistory.reduce((low, bar) => Math.min(low, bar.low ?? bar.close), Number.POSITIVE_INFINITY);
    const chartPriorIntradayHigh = chartData?.priorHigh?.intradayHigh ?? history.reduce((high, bar) => Math.max(high, bar.high ?? bar.close), 0);
    if (!Number.isFinite(rangeLow) || rangeLow <= 0) return null;
    return {
      fromReferenceHighPct: chartPriorIntradayHigh > 0 ? (latestClose / chartPriorIntradayHigh - 1) * 100 : null,
      fromRangeLowPct: (latestClose / rangeLow - 1) * 100
    };
  }, [chartData?.history, chartData?.priorHigh?.intradayHigh, chartSettings.chart.range, latestChartBar?.close]);
  const showChartLadderSide = fullscreen === "chart" && chartSettings.overlays.cpLadder;
  const chartRenderSettings = useMemo(() => {
    if (fullscreen !== "chart") return chartSettings;
    return mergeChartSettings({
      ...chartSettings,
      chart: {
        ...chartSettings.chart,
        height: Math.max(chartSettings.chart.height, 740)
      }
    });
  }, [chartSettings, fullscreen]);

  const buildCurrentPlanBundle = (status: "draft" | "active"): RulePlanBundle => {
    const planId = currentPlanId;
    const normalizedSteps = steps.map((step) => ({ ...step, id: step.id.startsWith(`${planId}-`) ? step.id : `${planId}-${step.id}` }));
    return {
      id: planId,
      name: planName,
      direction,
      instrumentId: selectedInstrumentId,
      referenceMode,
      referenceHigh,
      triggerPct: triggerDropPct,
      ladderCount,
      spacingPctPoint,
      spacingBasis,
      budgetMode,
      budgetCurrency,
      totalBudget,
      status,
      steps: normalizedSteps,
      allocations: normalizedSteps.flatMap((step, index) => {
        const baseStep = steps[index];
        return (stepAllocations[baseStep.id] ?? []).map((item) => ({
          id: stableAllocationId(planId, step.id, item.id),
          planId,
          stepId: step.id,
          assetTicker: item.assetTicker,
          assetName: item.assetName,
          labelColor: item.labelColor || undefined,
          weightPct: baseStep.budget > 0 ? (item.amount / baseStep.budget) * 100 : 0,
          plannedAmount: item.amount
        }));
      })
    };
  };

  const savePlanBundleNow = async (plan: RulePlanBundle, mode: "auto" | "manual" = "auto") => {
    latestPlanForSaveRef.current = plan;
    const serialized = JSON.stringify(plan);
    if (serialized === lastSavedPlanRef.current && mode === "auto") return true;

    const activeSave = planSaveInFlightRef.current;
    if (activeSave) {
      if (mode === "manual") {
        planManualSavePendingRef.current = true;
        await activeSave;
        planManualSavePendingRef.current = false;
        planSaveQueuedRef.current = false;
        return savePlanBundleNow(plan, "manual");
      }
      planSaveQueuedRef.current = true;
      setSaveStatus("Autosave queued...");
      return false;
    }

    const planToSave = mode === "auto" ? latestPlanForSaveRef.current ?? plan : plan;
    const request = (async () => {
      const planSignature = JSON.stringify(planToSave);
      setSaveStatus(mode === "manual" ? "Saving..." : "Autosaving...");
      try {
        const saved = await savePlan(planToSave, mode);
        lastSavedPlanRef.current = planSignature;
        setPlans((current) => [saved, ...current.filter((item) => item.id !== saved.id)]);
        setSelectedPlanId((current) => (!current || current === planToSave.id ? saved.id : current));
        if (latestPlanForSaveRef.current?.id === planToSave.id) {
          setSaveStatus(
            mode === "manual"
              ? saved.status === "active" ? "Active / Saved" : "Draft Saved"
              : `Autosaved ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
          );
        }
        return true;
      } catch (error) {
        if (latestPlanForSaveRef.current?.id === planToSave.id) {
          setSaveStatus(error instanceof Error ? error.message : mode === "manual" ? "Save failed" : "Autosave failed");
        }
        return false;
      }
    })();

    planSaveInFlightRef.current = request;
    const savedSuccessfully = await request;
    if (planSaveInFlightRef.current === request) planSaveInFlightRef.current = null;

    const hadQueuedSave = planSaveQueuedRef.current;
    planSaveQueuedRef.current = false;
    const latestPlan = latestPlanForSaveRef.current;
    if (savedSuccessfully && hadQueuedSave && !planManualSavePendingRef.current && latestPlan) {
      const latestSignature = JSON.stringify(latestPlan);
      if (latestSignature !== lastSavedPlanRef.current) void savePlanBundleNow(latestPlan, "auto");
    }

    return savedSuccessfully;
  };

  const persistPlan = async (status: "draft" | "active") => {
    if (!planNameInput.trim()) {
      setSaveStatus("Enter Plan Name");
      planNameInputRef.current?.focus();
      return false;
    }
    return savePlanBundleNow(buildCurrentPlanBundle(status), "manual");
  };

  useEffect(() => {
    if (!plansLoaded || !selectedPlanId || !planNameInput.trim()) return;
    const status = selectedPlan?.status === "active" ? "active" : "draft";
    const plan = buildCurrentPlanBundle(status);
    const serialized = JSON.stringify(plan);

    if (skipNextPlanAutosaveRef.current) {
      skipNextPlanAutosaveRef.current = false;
      lastSavedPlanRef.current = serialized;
      latestPlanForSaveRef.current = plan;
      return;
    }

    latestPlanForSaveRef.current = plan;
    if (serialized === lastSavedPlanRef.current) return;
    setSaveStatus("Autosave queued...");

    const timer = window.setTimeout(() => {
      void savePlanBundleNow(plan, "auto");
    }, 900);

    return () => window.clearTimeout(timer);
  }, [
    budgetCurrency,
    budgetMode,
    direction,
    ladderCount,
    planName,
    plansLoaded,
    referenceHigh,
    referenceMode,
    selectedInstrumentId,
    selectedPlan?.status,
    selectedPlanId,
    spacingBasis,
    spacingPctPoint,
    stepAllocations,
    steps,
    totalBudget,
    triggerDropPct
  ]);

  const forceSaveDashboard = async () => {
    window.dispatchEvent(new CustomEvent("cp-dashboard:save-started"));
    const status = selectedPlan?.status === "active" ? "active" : "draft";
    const plan = buildCurrentPlanBundle(status);
    const [planSaved, chartSaved] = await Promise.all([
      savePlanBundleNow(plan, "manual"),
      saveChartSettingsNow("manual")
    ]);

    window.dispatchEvent(new CustomEvent(planSaved && chartSaved ? "cp-dashboard:save-done" : "cp-dashboard:save-failed"));
  };

  useEffect(() => {
    const onForceSave = () => {
      void forceSaveDashboard();
    };
    window.addEventListener("cp-dashboard:force-save", onForceSave);
    return () => window.removeEventListener("cp-dashboard:force-save", onForceSave);
  });

  const startNewPlan = () => {
    const id = uniquePlanId(direction, selectedInstrumentId);
    setDraftPlanId(id);
    setSelectedPlanId(id);
    setPlanNameInput("");
    setSaveStatus("Enter Plan Name");
    setReferenceTouched(false);
    setStepBudgetOverrides({});
    setStepBudgetDrafts({});
    setStepAllocations(defaultStepAllocations(steps));
    setAllocationEditor(null);
    latestPlanForSaveRef.current = null;
    lastSavedPlanRef.current = "";
    planSaveQueuedRef.current = false;
    skipNextPlanAutosaveRef.current = false;
    window.setTimeout(() => planNameInputRef.current?.focus(), 0);
  };

  const removeCurrentPlan = async () => {
    if (!selectedPlanId) return;
    setSaveStatus("Deleting...");
    try {
      const next = await deletePlan(selectedPlanId);
      setPlans(next);
      if (next[0]) applyPlanToForm(next[0]);
      else startNewPlan();
      setSaveStatus("Plan Deleted");
    } catch (error) {
      setSaveStatus(error instanceof Error ? error.message : "Delete failed");
    }
  };

  const selectPlan = (planId: string) => {
    const plan = plans.find((item) => item.id === planId);
    if (plan) applyPlanToForm(plan);
  };

  const updateEditableNumber = (
    value: string,
    setText: (next: string) => void,
    setNumber: (next: number) => void,
    options: { min?: number; max?: number; integer?: boolean } = {},
    onValid?: () => void
  ) => {
    const parsed = editableNumber(value, options);
    if (parsed === null) {
      setText(value);
      return;
    }
    setText(parsed === Number(value) ? value : numberText(parsed));
    setNumber(parsed);
    onValid?.();
  };

  const commitEditableNumber = (
    value: string,
    fallback: number,
    setText: (next: string) => void,
    setNumber: (next: number) => void,
    options: { min?: number; max?: number; integer?: boolean } = {},
    onValid?: () => void
  ) => {
    const parsed = editableNumber(value, options) ?? fallback;
    const committed = cleanNumber(String(parsed), fallback, options);
    setNumber(committed);
    setText(numberText(committed));
    onValid?.();
  };

  const updateStepBudget = (stepId: string, field: StepBudgetDraft["field"], text: string) => {
    setStepBudgetDrafts((current) => ({ ...current, [stepId]: { field, text } }));
    const parsed = editableNumber(text, { min: 0, max: field === "percent" ? 100 : undefined });
    if (parsed === null && text.trim() !== "") return;
    setStepBudgetOverrides((current) => ({
      ...current,
      [stepId]: { basis: field, value: parsed ?? 0 }
    }));
  };

  const commitStepBudget = (stepId: string) => {
    setStepBudgetDrafts((current) => {
      const next = { ...current };
      delete next[stepId];
      return next;
    });
  };

  const rebalanceStepBudgets = () => {
    if (!steps.length) return;
    const equalBudget = totalBudget / steps.length;
    setStepBudgetOverrides(Object.fromEntries(steps.map((step) => [
      step.id,
      { basis: "amount", value: equalBudget } satisfies StepBudgetOverride
    ])));
    setStepBudgetDrafts({});
    setSaveStatus("Equal allocation applied");
  };

  const selectAssetDraft = (ticker: string) => {
    const asset = assetUniverse.find((item) => item.ticker === ticker);
    setAssetDraft(asset ?? { ...emptyAssetDraft, ticker });
  };

  const updateAssetRecord = (ticker: string, patch: Partial<AssetRecord>) => {
    setAssetUniverse((current) => current.map((asset) => (asset.ticker === ticker ? { ...asset, ...patch } : asset)));
    setAssetDraft((current) => (current.ticker === ticker ? { ...current, ...patch } : current));
  };

  const updateAssetWeight = (ticker: string, value: string) => {
    const parsed = editableNumber(value, { min: 0, max: 100 });
    if (parsed === null) {
      setAssetWeightInputs((current) => ({ ...current, [ticker]: value }));
      return;
    }
    setAssetWeightInputs((current) => ({ ...current, [ticker]: parsed === Number(value) ? value : numberText(parsed) }));
    updateAssetRecord(ticker, { defaultWeightPct: parsed });
    setAssetStatus("Unsaved asset changes");
  };

  const commitAssetWeight = (ticker: string) => {
    const asset = assetUniverse.find((item) => item.ticker === ticker);
    if (!asset) return;
    setAssetWeightInputs((current) => ({ ...current, [ticker]: numberText(asset.defaultWeightPct) }));
  };

  const addAssetColumn = () => {
    const nextNumber = assetUniverse.length + 1;
    let ticker = `NEW-${nextNumber}`;
    while (assetUniverse.some((asset) => asset.ticker === ticker)) {
      ticker = `NEW-${Math.floor(Math.random() * 9000) + 1000}`;
    }
    const asset: AssetRecord = {
      ...emptyAssetDraft,
      ticker,
      name: "New Allocation",
      defaultWeightPct: 0,
      active: true
    };
    setAssetUniverse((current) => [...current, asset]);
    setAssetWeightInputs((current) => ({ ...current, [ticker]: "0" }));
    setAssetDraft(asset);
    setAssetStatus("New asset column added");
  };

  const updateAssetAtIndex = (index: number, patch: Partial<AssetRecord>) => {
    const currentAsset = assetUniverse[index];
    if (!currentAsset) return;
    const nextAsset = { ...currentAsset, ...patch };
    setAssetUniverse((current) => current.map((asset, assetIndex) => (assetIndex === index ? nextAsset : asset)));
    setAssetDraft((current) => (current.ticker === currentAsset.ticker ? nextAsset : current));
    if (patch.ticker !== undefined && patch.ticker !== currentAsset.ticker) {
      setAssetWeightInputs((current) => {
        const { [currentAsset.ticker]: oldValue, ...rest } = current;
        return { ...rest, [patch.ticker || currentAsset.ticker]: oldValue ?? numberText(nextAsset.defaultWeightPct) };
      });
    }
    setAssetStatus("Unsaved asset changes");
  };

  const updateAssetWeightAtIndex = (index: number, value: string) => {
    const asset = assetUniverse[index];
    if (!asset) return;
    const parsed = editableNumber(value, { min: 0, max: 100 });
    if (parsed === null) {
      setAssetWeightInputs((current) => ({ ...current, [asset.ticker]: value }));
      return;
    }
    setAssetWeightInputs((current) => ({ ...current, [asset.ticker]: parsed === Number(value) ? value : numberText(parsed) }));
    updateAssetAtIndex(index, { defaultWeightPct: parsed });
  };

  const commitAssetWeightAtIndex = (index: number) => {
    const asset = assetUniverse[index];
    if (!asset) return;
    setAssetWeightInputs((current) => ({ ...current, [asset.ticker]: numberText(asset.defaultWeightPct) }));
  };

  const persistAssetRecord = async (asset: AssetRecord) => {
    if (!asset.ticker.trim() || !asset.name.trim()) {
      setAssetStatus("Ticker and name required");
      return;
    }
    setAssetStatus("Saving asset...");
    try {
      const normalized = {
        ...asset,
        ticker: asset.ticker.trim(),
        name: asset.name.trim(),
        currency: asset.currency.toUpperCase(),
        active: true
      };
      const assets = await saveAsset(normalized);
      setAssetUniverse(assets);
      setAssetWeightInputs(Object.fromEntries(assets.map((item) => [item.ticker, numberText(item.defaultWeightPct)])));
      setAssetDraft(assets.find((item) => item.ticker === normalized.ticker) ?? assets[0] ?? emptyAssetDraft);
      setAssetStatus("Asset saved");
    } catch (error) {
      setAssetStatus(error instanceof Error ? error.message : "Asset save failed");
    }
  };

  const removeAssetByTicker = async (ticker: string) => {
    if (!ticker) return;
    setAssetStatus("Deleting asset...");
    try {
      const assets = await deleteAsset(ticker);
      setAssetUniverse(assets);
      setAssetWeightInputs(Object.fromEntries(assets.map((asset) => [asset.ticker, numberText(asset.defaultWeightPct)])));
      setAssetDraft(assets[0] ?? emptyAssetDraft);
      setAssetStatus("Asset deleted");
    } catch (error) {
      setAssetStatus(error instanceof Error ? error.message : "Asset delete failed");
    }
  };

  const persistAsset = async () => {
    await persistAssetRecord(assetDraft);
  };

  const removeAssetDraft = async () => {
    await removeAssetByTicker(assetDraft.ticker);
  };

  const openAllocationEditor = (stepId: string, item?: StepAllocationItem) => {
    const asset = item ? allocationAssets.find((candidate) => candidate.ticker === item.assetTicker) : null;
    setAllocationEditor({
      mode: item ? "edit" : "add",
      stepId,
      itemId: item?.id,
      assetTicker: item?.assetTicker ?? "",
      assetName: item?.assetName ?? "",
      labelColor: item?.labelColor ?? asset?.color ?? defaultAllocationLabelColor,
      amountText: item ? numberText(item.amount) : ""
    });
  };

  const saveAllocationEditor = () => {
    if (!allocationEditor) return;
    const step = steps.find((item) => item.id === allocationEditor.stepId);
    const assetTicker = allocationEditor.assetTicker.trim();
    const assetName = allocationEditor.assetName.trim() || assetTicker;
    const amount = editableNumber(allocationEditor.amountText, { min: 0 }) ?? 0;
    if (!assetTicker || !step || amount <= 0) {
      setAssetStatus("Asset label and positive amount required");
      return;
    }

    const nextItem: StepAllocationItem = {
      id: allocationEditor.itemId ?? allocationItemId(allocationEditor.stepId, assetTicker),
      stepId: allocationEditor.stepId,
      assetTicker,
      assetName,
      labelColor: allocationEditor.labelColor,
      amount
    };

    setStepAllocations((current) => {
      const items = current[allocationEditor.stepId] ?? [];
      const nextItems = allocationEditor.mode === "edit" && allocationEditor.itemId
        ? items.map((item) => (item.id === allocationEditor.itemId ? nextItem : item))
        : [...items, nextItem];
      return { ...current, [allocationEditor.stepId]: nextItems };
    });
    setAllocationEditor(null);
    setAssetStatus("Allocation updated");
  };

  const deleteAllocationItem = (stepId: string, itemId: string) => {
    setStepAllocations((current) => ({
      ...current,
      [stepId]: (current[stepId] ?? []).filter((item) => item.id !== itemId)
    }));
    setAssetStatus("Allocation removed");
  };

  const duplicateAllocationToStep = (targetStepId: string, item: StepAllocationItem) => {
    if (targetStepId === item.stepId) return;
    const duplicate: StepAllocationItem = {
      ...item,
      id: allocationItemId(targetStepId, item.assetTicker),
      stepId: targetStepId
    };
    setStepAllocations((current) => ({
      ...current,
      [targetStepId]: [...(current[targetStepId] ?? []), duplicate]
    }));
    setAssetStatus(`Duplicated ${item.assetTicker} to ${steps.find((step) => step.id === targetStepId)?.label ?? "step"}`);
  };

  const handleChipDragStart = (event: DragEvent<HTMLDivElement>, item: StepAllocationItem) => {
    event.dataTransfer.effectAllowed = "copy";
    event.dataTransfer.setData("application/json", JSON.stringify(item));
  };

  const handleStepDrop = (event: DragEvent<HTMLDivElement>, targetStepId: string) => {
    event.preventDefault();
    setAllocationDragOverStep(null);
    const payload = event.dataTransfer.getData("application/json");
    if (!payload) return;
    try {
      duplicateAllocationToStep(targetStepId, JSON.parse(payload) as StepAllocationItem);
    } catch {
      setAssetStatus("Could not duplicate allocation");
    }
  };

  const updateChartSettings = (updater: (settings: ChartSettings) => ChartSettings) => {
    setChartSettings((current) => mergeChartSettings(updater(current)));
  };

  const updateChartDisplay = (patch: Partial<ChartSettings["chart"]>) => {
    updateChartSettings((current) => ({
      ...current,
      chart: {
        ...current.chart,
        ...patch
      }
    }));
  };

  const toggleOverlay = (key: keyof ChartSettings["overlays"]) => {
    updateChartSettings((current) => ({
      ...current,
      overlays: { ...current.overlays, [key]: !current.overlays[key] }
    }));
  };

  const toggleMovingAverage = (key: MovingAverageKey) => {
    updateChartSettings((current) => ({
      ...current,
      movingAverages: { ...current.movingAverages, [key]: !current.movingAverages[key] }
    }));
  };

  const setCustomMovingAverage = (value: string) => {
    setCustomMaInput(value);
  };

  const commitCustomMovingAverage = () => {
    if (customMaInput.trim() === "") return;
    const committed = cleanNumber(customMaInput, chartSettings.movingAverages.customPeriod, { min: 2, max: 500, integer: true });
    setCustomMaInput(numberText(committed));
    updateChartSettings((current) => ({
      ...current,
      movingAverages: {
        ...current.movingAverages,
        customPeriod: committed
      }
    }));
  };

  const updateLineStyle = (key: ChartStyleKey, patch: Partial<ChartLineStyleConfig>) => {
    updateChartSettings((current) => ({
      ...current,
      styles: {
        ...current.styles,
        [key]: {
          ...current.styles[key],
          ...patch,
          width: patch.width === undefined ? current.styles[key].width : Math.max(0.5, Math.min(patch.width, 6))
        }
      }
    }));
  };

  const resetChartSettings = () => {
    const reset = mergeChartSettings({ ...defaultChartSettings, scope: chartSettings.scope });
    setCustomMaInput(numberText(reset.movingAverages.customPeriod));
    setChartSettings(reset);
    setChartSettingsStatus("Reset pending autosave...");
  };

  return (
    <section className={`dashboard ${isPlansWorkspace ? "plans-editor-dashboard" : ""} ${fullscreen ? "has-fullscreen" : ""}`}>
      <Panel id="builder" letter="A" title="Plan Builder Command Center" subtitle="Design CP / Reverse CP ladder based on prior highs" fullscreen={fullscreen} setFullscreen={setFullscreen}>
        <div className="section pad">
          <div className="control-grid plan-control-grid">
            <label className="field"><span>Index</span><select value={selectedInstrumentId} onChange={(event) => { setSelectedInstrumentId(event.target.value); setReferenceTouched(false); }}>{instrumentOptions.map((instrument) => <option key={instrument.id} value={instrument.id}>{instrument.name} ({instrument.ticker})</option>)}</select></label>
            <label className="field"><span>Saved Plan</span><select value={selectedPlan?.id ?? selectedPlanId} onChange={(event) => selectPlan(event.target.value)}>{plans.map((plan) => <option key={plan.id} value={plan.id}>{plan.name}</option>)}{!selectedPlan && selectedPlanId && <option value={selectedPlanId}>{planName}</option>}</select></label>
            <label className="field"><span>Plan Name</span><input ref={planNameInputRef} type="text" value={planNameInput} placeholder={`${direction} Plan - ${selectedInstrument.ticker}`} onChange={(event) => setPlanNameInput(event.target.value)} /></label>
            <span className="active-pill">{saveStatus.startsWith("Saving") ? "Saving" : saveStatus}</span>
          </div>
          <div className="plan-crud-row">
            <button className="btn" type="button" onClick={startNewPlan}><Plus /> New Plan</button>
            <button className="btn" type="button" onClick={() => void persistPlan("draft")}><Save /> Save Draft</button>
            <button className="btn primary" type="button" onClick={() => void persistPlan("active")}>Activate Plan</button>
            <button className="btn danger" type="button" onClick={() => void removeCurrentPlan()} disabled={!selectedPlan}><Trash2 /> Delete</button>
          </div>
        </div>

        {isPlansWorkspace && (
          <div className="section pad plans-compact-settings">
            <div className="plans-compact-grid">
              <div className="plans-compact-control plans-direction-control">
                <span className="mini-label">Direction</span>
                <div className="compact-choice-row">
                  <button className={`compact-choice ${direction === "CP" ? "is-active" : ""}`} type="button" onClick={() => setDirection("CP")}>CP / Buy</button>
                  <button className={`compact-choice ${direction === "RCP" ? "is-active" : ""}`} type="button" onClick={() => setDirection("RCP")}>RCP / Sell</button>
                </div>
              </div>
              <div className="plans-compact-control plans-reference-control">
                <span className="mini-label">Reference</span>
                <div className="compact-choice-row">
                  <button className={`compact-choice ${referenceMode === "intraday-high" ? "is-active" : ""}`} type="button" onClick={() => { setReferenceMode("intraday-high"); setReferenceTouched(false); }}>Intraday High</button>
                  <button className={`compact-choice ${referenceMode === "close-high" ? "is-active" : ""}`} type="button" onClick={() => { setReferenceMode("close-high"); setReferenceTouched(false); }}>Close High</button>
                </div>
              </div>
              <label className="field"><span>Reference High</span><input inputMode="decimal" type="text" value={referenceHighText} onChange={(event) => updateEditableNumber(event.target.value, setReferenceHighText, setReferenceHigh, { min: 0 }, () => setReferenceTouched(true))} onBlur={() => commitEditableNumber(referenceHighText, referenceHigh, setReferenceHighText, setReferenceHigh, { min: 0 }, () => setReferenceTouched(true))} /></label>
              <label className="field"><span>Trigger</span><input inputMode="decimal" type="text" value={triggerDropPctText} onChange={(event) => updateEditableNumber(event.target.value, setTriggerDropPctText, setTriggerDropPct, { min: 0 })} onBlur={() => commitEditableNumber(triggerDropPctText, triggerDropPct, setTriggerDropPctText, setTriggerDropPct, { min: 0 })} /><em>%</em></label>
              <label className="field"><span>Ladder Count</span><input inputMode="numeric" type="text" value={ladderCountText} onChange={(event) => updateEditableNumber(event.target.value, setLadderCountText, setLadderCount, { min: 1, max: 200, integer: true })} onBlur={() => commitEditableNumber(ladderCountText, ladderCount, setLadderCountText, setLadderCount, { min: 1, max: 200, integer: true })} /><em>Steps</em></label>
              <label className="field"><span>Spacing</span><input inputMode="decimal" type="text" value={spacingPctPointText} onChange={(event) => updateEditableNumber(event.target.value, setSpacingPctPointText, setSpacingPctPoint, { min: 0 })} onBlur={() => commitEditableNumber(spacingPctPointText, spacingPctPoint, setSpacingPctPointText, setSpacingPctPoint, { min: 0 })} /><em>%pt</em></label>
              <label className="field plans-spacing-basis"><span>Spacing Basis</span><select value={spacingBasis} onChange={(event) => setSpacingBasis(event.target.value as "prior-high" | "previous-ladder")}><option value="prior-high">From reference high</option><option value="previous-ladder">From previous ladder</option></select></label>
              <div className="plans-reference-date"><span className="mini-label">Reference Date</span><b>{referenceCandidate?.date ?? "Cached"}</b></div>
            </div>
            <div className="plans-compact-summary">
              <span>{steps.length} steps</span>
              <span>Trigger {pct(steps[0]?.dropPct ?? 0)}</span>
              <span>Final {pct(steps.at(-1)?.dropPct ?? 0)}</span>
              <span>WAI {number(plannedWai)}</span>
            </div>
          </div>
        )}

        {!isPlansWorkspace && <>
        <div className="section pad">
          <div className="segmented">
            <button className={`mode-card ${direction === "CP" ? "is-active" : ""}`} onClick={() => setDirection("CP")}>
              <b>Contingency Plan (Buy)</b>
              Buy on decline
            </button>
            <button className={`mode-card ${direction === "RCP" ? "is-active" : ""}`} onClick={() => setDirection("RCP")}>
              <b>Reverse CP (Sell)</b>
              Sell on advance
            </button>
          </div>
        </div>

        <div className="section pad">
          <div className="section-title">Prior High Reference</div>
          <div className="segmented compact">
            <button className={`mode-card ${referenceMode === "intraday-high" ? "is-active" : ""}`} onClick={() => { setReferenceMode("intraday-high"); setReferenceTouched(false); }}><b>Intraday High</b> Session high</button>
            <button className={`mode-card ${referenceMode === "close-high" ? "is-active" : ""}`} onClick={() => { setReferenceMode("close-high"); setReferenceTouched(false); }}><b>Close High</b> End of day close</button>
          </div>
          <div className="reference-grid">
            <div className="ref-cell"><span className="mini-label">Reference High</span><label className="field compact-ref"><input type="text" inputMode="decimal" value={referenceHighText} onChange={(event) => updateEditableNumber(event.target.value, setReferenceHighText, setReferenceHigh, { min: 0 }, () => setReferenceTouched(true))} onBlur={() => commitEditableNumber(referenceHighText, referenceHigh, setReferenceHighText, setReferenceHigh, { min: 0 }, () => setReferenceTouched(true))} /></label></div>
            <div className="ref-cell"><span className="mini-label">Reference Date</span><div className="small-number strong">{referenceCandidate?.date ?? "Cached"}</div></div>
            <div className="ref-cell"><span className="mini-label">Session</span><div className="small-number strong">Regular (RTH)</div></div>
          </div>
        </div>

        <div className="section pad">
          <div className="section-title">Trigger & Ladder Settings</div>
          <div className="setting-row">
            <label className="field"><span>Trigger (% from prior high)</span><input type="text" inputMode="decimal" value={triggerDropPctText} onChange={(event) => updateEditableNumber(event.target.value, setTriggerDropPctText, setTriggerDropPct, { min: 0 })} onBlur={() => commitEditableNumber(triggerDropPctText, triggerDropPct, setTriggerDropPctText, setTriggerDropPct, { min: 0 })} /><em>%</em></label>
            <label className="field"><span>Ladder Count</span><input type="text" inputMode="numeric" value={ladderCountText} onChange={(event) => updateEditableNumber(event.target.value, setLadderCountText, setLadderCount, { min: 1, max: 200, integer: true })} onBlur={() => commitEditableNumber(ladderCountText, ladderCount, setLadderCountText, setLadderCount, { min: 1, max: 200, integer: true })} /><em>Steps</em></label>
            <label className="field"><span>Ladder Spacing</span><input type="text" inputMode="decimal" value={spacingPctPointText} onChange={(event) => updateEditableNumber(event.target.value, setSpacingPctPointText, setSpacingPctPoint, { min: 0 })} onBlur={() => commitEditableNumber(spacingPctPointText, spacingPctPoint, setSpacingPctPointText, setSpacingPctPoint, { min: 0 })} /><em>%pt</em></label>
          </div>
          <div className="segmented compact">
            <button className={`mode-card ${spacingBasis === "prior-high" ? "is-active" : ""}`} onClick={() => setSpacingBasis("prior-high")}>
              <b>Spacing from Prior High</b>
              Each ladder uses the original reference high
            </button>
            <button className={`mode-card ${spacingBasis === "previous-ladder" ? "is-active" : ""}`} onClick={() => setSpacingBasis("previous-ladder")}>
              <b>Spacing from Previous Ladder</b>
              Each ladder compounds from the prior step
            </button>
          </div>
          <div className="ladder-visual">
            {steps.map((step) => (
              <div className="ladder-row" key={step.id}>
                <b className={step.id === "trigger" ? "red" : "blue"}>{step.label}</b>
                <span className="ladder-line"><span className={`node ${step.id === "trigger" ? "trigger" : ""}`} /><span className="dash" /></span>
                <span className={step.id === "trigger" ? "red" : "blue"}>{pct(step.dropPct)}</span>
                <span className="num">{number(step.indexLevel)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="section">
          <div className="summary-grid">
            <Summary label="Direction" value={direction === "CP" ? "Buy on Decline" : "Sell on Advance"} />
            <Summary label="Total Steps" value={`${steps.length}`} />
            <Summary label="Trigger" value={pct(steps[0].dropPct)} />
            <Summary label="Max Ladder" value={pct(steps[steps.length - 1].dropPct)} />
            <Summary label="Planned WAI" value={number(plannedWai)} />
            <Summary label="Status" value={saveStatus} amber />
          </div>
          <div className="footer-actions">
            <button className="btn" onClick={() => { setDirection("CP"); setSpacingBasis("prior-high"); setTriggerDropPct(5); setTriggerDropPctText("5"); setLadderCount(5); setLadderCountText("5"); setSpacingPctPoint(2); setSpacingPctPointText("2"); setReferenceHigh(5350); setReferenceHighText("5350"); setReferenceTouched(false); setTotalBudgetInput(100000); setTotalBudgetText("100000"); setBudgetCurrency("USD"); setStepBudgetOverrides({}); setStepBudgetDrafts({}); }}><RotateCcw /> Reset</button>
            <button className="btn" onClick={() => void persistPlan("draft")}><Save /> Save Draft</button>
            <button className="btn primary" onClick={() => void persistPlan("active")}>Apply to Chart & Allocation</button>
          </div>
        </div>
        </>}
      </Panel>

      <Panel id="allocation" letter="B" title="Allocation Matrix" subtitle="Allocate budget by ladder and distribute to assets" fullscreen={fullscreen} setFullscreen={setFullscreen}>
        <div className="section pad">
          <div className="allocation-controls">
            <label className="field"><span>Plan</span><select value={selectedPlan?.id ?? selectedPlanId} onChange={(event) => selectPlan(event.target.value)}>{plans.map((plan) => <option key={plan.id} value={plan.id}>{plan.name}</option>)}{!selectedPlan && selectedPlanId && <option value={selectedPlanId}>{planName}</option>}</select></label>
            <div className="field">
              <span>Budget Mode</span>
              <div className="radio-row">
                <button className={`radio-chip ${budgetMode === "amount" ? "is-active" : ""}`} onClick={() => setBudgetMode("amount")}><span className="radio-dot" /> Amount</button>
                <button className={`radio-chip ${budgetMode === "percent" ? "is-active" : ""}`} onClick={() => setBudgetMode("percent")}><span className="radio-dot" /> Percent (%)</button>
              </div>
            </div>
            <label className="field"><span>Currency</span><select value={budgetCurrency} onChange={(event) => setBudgetCurrency(event.target.value as BudgetCurrency)}><option value="USD">USD</option><option value="KRW">KRW</option></select></label>
            <label className="field"><span>Total Budget</span><input type="text" inputMode="decimal" value={totalBudgetText} onChange={(event) => updateEditableNumber(event.target.value, setTotalBudgetText, setTotalBudgetInput, { min: 0 })} onBlur={() => commitEditableNumber(totalBudgetText, totalBudgetInput, setTotalBudgetText, setTotalBudgetInput, { min: 0 })} /></label>
          </div>
        </div>
        <div className="stat-strip">
          <Stat label="Steps" value={`${steps.length}`} />
          <Stat label="Total Budget" value={money(totalBudget, budgetCurrency)} subValue={`(${totalBudget > 0 ? "100.0" : "0.0"}%)`} />
          <Stat label="Allocated" value={money(allocatedBudget, budgetCurrency)} subValue={`(${allocationWeightTotal.toFixed(1)}%)`} />
          <Stat label="Remaining" value={money(remainingBudget, budgetCurrency)} subValue={`(${remainingWeightTotal.toFixed(1)}%)`} />
          <Stat label="Planned WAI" value={number(plannedWai)} subValue={`(${pct(plannedWaiReferencePct)})`} />
        </div>
        {isPlansWorkspace && (
          <div className="section plans-step-allocation-section">
            <div className="section-subhead">
              <span>Step Budgets &amp; Asset Allocation</span>
              <div className="plans-allocation-head-actions">
                <span className="subhead-status">{assetStatus}</span>
                <button className="mini-btn rebalance-button" type="button" onClick={rebalanceStepBudgets} title="Reset every step to an equal share of Total Budget"><RotateCcw /> Rebalance</button>
              </div>
            </div>
            {!plansLoaded ? <div className="ledger-loading">Loading server allocations...</div> : (
              <div className="plans-step-allocation-list">
                <div className="plans-step-column-head" aria-hidden="true"><span>Step / Level / Budget</span><span>Asset Allocation</span></div>
                {steps.map((step) => {
                  const draft = stepBudgetDrafts[step.id];
                  const stepPercent = totalBudget > 0 ? (step.budget / totalBudget) * 100 : 0;
                  const items = stepAllocations[step.id] ?? [];
                  const stepAllocated = items.reduce((sum, item) => sum + item.amount, 0);
                  const stepAssetPct = step.budget > 0 ? (stepAllocated / step.budget) * 100 : 0;
                  const overBudget = stepAllocated > step.budget;
                  return (
                    <div
                      className={`plans-step-allocation-row ${allocationDragOverStep === step.id ? "is-drag-over" : ""} ${overBudget ? "is-over-budget" : ""}`}
                      key={step.id}
                      onDragLeave={() => setAllocationDragOverStep(null)}
                      onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = "copy"; setAllocationDragOverStep(step.id); }}
                      onDrop={(event) => handleStepDrop(event, step.id)}
                    >
                      <div className="plans-step-budget-side">
                        <div className="plans-step-identity">
                          <b className={step.id === "trigger" ? "red" : "blue"}>{step.label}</b>
                          <span>{pct(step.dropPct)}</span>
                          <strong>{number(step.indexLevel)}</strong>
                        </div>
                        <div className="plans-step-budget-inputs">
                          <label><span>Budget</span><input aria-label={`${step.label} budget`} className="budget-cell-input" inputMode="decimal" type="text" value={draft?.field === "amount" ? draft.text : budgetInputText(step.budget)} onChange={(event) => updateStepBudget(step.id, "amount", event.target.value)} onBlur={() => commitStepBudget(step.id)} /></label>
                          <label><span>% Total</span><div className="percent-input-wrap"><input aria-label={`${step.label} percent of total`} className="budget-cell-input" inputMode="decimal" type="text" value={draft?.field === "percent" ? draft.text : percentInputText(stepPercent)} onChange={(event) => updateStepBudget(step.id, "percent", event.target.value)} onBlur={() => commitStepBudget(step.id)} /><span>%</span></div></label>
                        </div>
                      </div>
                      <div className="plans-step-assets-side">
                        <div className="step-ledger-chips">
                          {items.map((item) => {
                            const asset = allocationAssets.find((candidate) => candidate.ticker === item.assetTicker);
                            const color = item.labelColor || asset?.color || defaultAllocationLabelColor;
                            const itemPct = step.budget > 0 ? (item.amount / step.budget) * 100 : 0;
                            return (
                              <div className="step-allocation-chip is-expanded-view" draggable key={item.id} onDragStart={(event) => handleChipDragStart(event, item)} style={{ borderColor: color }} title="Drag to another step to duplicate">
                                <span className="chip-marker" style={{ background: color }} />
                                <span className="chip-copy"><b>{item.assetTicker}</b><em>{money(item.amount, budgetCurrency)} / {Math.round(itemPct)}%</em></span>
                                <span className="chip-actions"><button type="button" onClick={() => openAllocationEditor(step.id, item)}>Edit</button><button type="button" onClick={() => deleteAllocationItem(step.id, item.id)}>Delete</button></span>
                              </div>
                            );
                          })}
                          <button className="chip-add-button" type="button" onClick={() => openAllocationEditor(step.id)}><Plus /> Asset</button>
                        </div>
                        <div className="plans-step-assets-total"><b className={overBudget ? "red" : ""}>{money(stepAllocated, budgetCurrency)}</b><span>{stepAssetPct.toFixed(1)}% of step</span></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {!isPlansWorkspace && <>
        <div className="section">
          <div className="section-subhead">
            <span>Ladder Budget Allocation</span>
            <button className="mini-btn rebalance-button" type="button" onClick={rebalanceStepBudgets} title="Reset every step to an equal share of Total Budget">
              <RotateCcw /> Rebalance
            </button>
          </div>
          <div className="table-wrap step-budget-scroll">
            <table className="budget-table">
              <thead><tr><th>Step</th><th>Level</th><th>Level Price</th><th>Budget</th><th>% of Total</th></tr></thead>
              <tbody>
                {steps.map((step) => {
                  const draft = stepBudgetDrafts[step.id];
                  const stepPercent = totalBudget > 0 ? (step.budget / totalBudget) * 100 : 0;
                  return (
                  <tr key={step.id}>
                    <td>{step.label}</td>
                    <td className={step.id === "trigger" ? "red num" : "blue num"}>{pct(step.dropPct)}</td>
                    <td className="num">{number(step.indexLevel)}</td>
                    <td className="num budget-edit-cell">
                      <input aria-label={`${step.label} budget`} className="budget-cell-input" inputMode="decimal" type="text" value={draft?.field === "amount" ? draft.text : budgetInputText(step.budget)} onChange={(event) => updateStepBudget(step.id, "amount", event.target.value)} onBlur={() => commitStepBudget(step.id)} />
                    </td>
                    <td className="num budget-edit-cell">
                      <div className="percent-input-wrap"><input aria-label={`${step.label} percent of total`} className="budget-cell-input" inputMode="decimal" type="text" value={draft?.field === "percent" ? draft.text : percentInputText(stepPercent)} onChange={(event) => updateStepBudget(step.id, "percent", event.target.value)} onBlur={() => commitStepBudget(step.id)} /><span>%</span></div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              <tfoot><tr><th colSpan={3}>Ladder Total</th><th className="num">{money(ladderBudgetTotal, budgetCurrency)}</th><th className="num">{totalBudget > 0 ? ((ladderBudgetTotal / totalBudget) * 100).toFixed(1) : "0.0"}%</th></tr></tfoot>
            </table>
          </div>
        </div>
        <div className="section">
          <div className="section-subhead">
            <span>Asset Allocation per Step</span>
            <span className="subhead-status">{assetStatus}</span>
          </div>
          {!plansLoaded ? (
            <div className="ledger-loading">Loading server allocations...</div>
          ) : (
          <div className="step-ledger-list">
            {steps.map((step) => {
              const items = stepAllocations[step.id] ?? [];
              const stepAllocated = items.reduce((sum, item) => sum + item.amount, 0);
              const stepPct = step.budget > 0 ? (stepAllocated / step.budget) * 100 : 0;
              const overBudget = stepAllocated > step.budget;

              return (
                <div
                  className={`step-ledger-row ${allocationDragOverStep === step.id ? "is-drag-over" : ""} ${overBudget ? "is-over-budget" : ""}`}
                  key={step.id}
                  onDragLeave={() => setAllocationDragOverStep(null)}
                  onDragOver={(event) => {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "copy";
                    setAllocationDragOverStep(step.id);
                  }}
                  onDrop={(event) => handleStepDrop(event, step.id)}
                >
                  <div className="step-ledger-meta">
                    <b className={step.id === "trigger" ? "red" : "blue"}>{step.label}</b>
                    <span>{pct(step.dropPct)}</span>
                    <span>{number(step.indexLevel)}</span>
                  </div>
                  <div className="step-ledger-chips">
                    {items.map((item) => {
                      const asset = allocationAssets.find((candidate) => candidate.ticker === item.assetTicker);
                      const color = item.labelColor || asset?.color || defaultAllocationLabelColor;
                      const itemPct = step.budget > 0 ? (item.amount / step.budget) * 100 : 0;

                      return (
                        <div
                          className={`step-allocation-chip ${allocationPanelExpanded ? "is-expanded-view" : "is-compact-view"}`}
                          draggable
                          key={item.id}
                          onDragStart={(event) => handleChipDragStart(event, item)}
                          style={{ borderColor: color, backgroundColor: allocationPanelExpanded ? undefined : color }}
                          title="Drag to another step to duplicate"
                        >
                          <span className="chip-marker" style={{ background: color }} />
                          <span className="chip-copy">
                            <b>{item.assetTicker}</b>
                            <em>{allocationPanelExpanded ? `${money(item.amount, budgetCurrency)} / ${Math.round(itemPct)}%` : `${Math.round(itemPct)}%`}</em>
                          </span>
                          <span className="chip-actions">
                            <button type="button" onClick={() => openAllocationEditor(step.id, item)}>Edit</button>
                            <button type="button" onClick={() => deleteAllocationItem(step.id, item.id)}>Delete</button>
                          </span>
                        </div>
                      );
                    })}
                    <button className="chip-add-button" type="button" onClick={() => openAllocationEditor(step.id)}>
                      <Plus /> Asset
                    </button>
                  </div>
                  <div className="step-ledger-total">
                    <b className={overBudget ? "red" : ""}>{money(stepAllocated, budgetCurrency)}</b>
                    <span>{stepPct.toFixed(1)}% of step</span>
                    <small>{money(step.budget, budgetCurrency)} budget</small>
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </div>
        </>}
        <div className="allocation-footer">
          <span>Data status: {dataStatus}</span>
          <span className="switch-label">Auto-balance <span className="switch" /></span>
          <button className="btn">Rebalance by %</button>
        </div>
      </Panel>

      {!isPlansWorkspace && <Panel id="chart" letter="C" title="Chart Intelligence" subtitle="Visualize triggers, ladders, and technical indicators" fullscreen={fullscreen} setFullscreen={setFullscreen}>
        <div className="section pad">
          <div className="chart-controls">
            <label className="field"><span>Instrument</span><select value={selectedInstrumentId} onChange={(event) => setSelectedInstrumentId(event.target.value)}>{instrumentOptions.map((instrument) => <option key={instrument.id} value={instrument.id}>{instrument.name} ({instrument.ticker})</option>)}</select></label>
            <label className="field"><span>View</span><select value={chartSettings.chart.view} onChange={(event) => updateChartDisplay({ view: event.target.value as ChartSettings["chart"]["view"] })}><option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option></select></label>
            <label className="field"><span>Range</span><select value={chartSettings.chart.range} onChange={(event) => updateChartDisplay({ range: event.target.value as ChartSettings["chart"]["range"] })}><option value="3M">3M</option><option value="6M">6M</option><option value="1Y">1Y</option><option value="3Y">3Y</option><option value="10Y">10Y</option></select></label>
            <button className={`btn ${chartSettingsOpen ? "is-active" : ""}`} type="button" onClick={() => setChartSettingsOpen((value) => !value)}><SlidersHorizontal /> Settings</button>
          </div>
        </div>
        {chartSettingsOpen && (
          <ChartSettingsPanel
            settings={chartSettings}
            status={chartSettingsStatus}
            onChartChange={updateChartDisplay}
            onToggleOverlay={toggleOverlay}
            onToggleMovingAverage={toggleMovingAverage}
            onReset={resetChartSettings}
          />
        )}
        <div className="chart-toolbar">
          <div><h3>{selectedInstrument.name} ({selectedInstrument.ticker}) - {chartSettings.chart.view.toUpperCase()}</h3><p>Last {chartData?.quote ? number(chartData.quote.price, chartData.quote.price > 1000 ? 0 : 2) : "Cached"} <span className={(chartData?.quote?.changePct ?? 0) < 0 ? "red" : "green"}>{chartData?.quote ? pct(chartData.quote.changePct) : ""}</span></p></div>
          <div className="icon-actions"><button className="icon-btn">Indicators</button><button className="icon-btn">Draw</button></div>
        </div>
        <div className={`chart-area single-chart ${showChartLadderSide ? "" : "no-ladder"}`}>
          <div className="price-chart">
            {chartDistanceStats && (
              <div className="chart-distance-badges" aria-label="Latest chart distance metrics">
                {chartDistanceStats.fromReferenceHighPct !== null && (
                  <span className="chart-distance-pill">
                    <b>vs Ref High</b>
                    <em className={chartDistanceStats.fromReferenceHighPct < 0 ? "red" : "green"}>{pct(chartDistanceStats.fromReferenceHighPct)}</em>
                  </span>
                )}
                <span className="chart-distance-pill">
                  <b>from Range Low</b>
                  <em className="green">{pct(chartDistanceStats.fromRangeLowPct)}</em>
                </span>
              </div>
            )}
            {chartData && hasVerifiedChartData ? (
              <Suspense fallback={<ChartSkeleton label="Preparing chart renderer" />}>
                <PriceIntelligenceChart steps={steps} history={chartData.history} referenceHigh={referenceHigh} visibleMas={chartSettings.movingAverages} customMa={chartSettings.movingAverages.customPeriod} chartSettings={chartRenderSettings} />
              </Suspense>
            ) : chartData ? (
              <div className="echarts-loading">
                <b>No verified OHLC data</b>
                <span>Synthetic candle fallback is disabled for chart accuracy.</span>
              </div>
            ) : (
              <ChartSkeleton label="Loading verified market data" />
            )}
          </div>
          {showChartLadderSide && <aside className="ladder-side">
            <div className="section-title">CP Ladder Overlay</div>
            <div className="side-list">
              <div className="side-ladder-row"><span className="tiny-line muted-line" /><span>Prior High</span><b>{number(referenceHigh)}</b></div>
              {steps.map((step) => (
                <div className={`side-ladder-row ${step.id === "trigger" ? "red" : "blue"}`} key={step.id}>
                  <span className="tiny-line" />
                  <span>{step.label} {pct(step.dropPct)}</span>
                  <b>{number(step.indexLevel)}</b>
                </div>
              ))}
            </div>
          </aside>}
        </div>
        <div className="chart-options">
          <OptionCard title="Moving Averages">
            <Check label="MA 20" color={chartSettings.styles.ma20.color} on={chartSettings.movingAverages.ma20} onClick={() => toggleMovingAverage("ma20")} />
            <Check label="MA 50" color={chartSettings.styles.ma50.color} on={chartSettings.movingAverages.ma50} onClick={() => toggleMovingAverage("ma50")} />
            <Check label="MA 200" color={chartSettings.styles.ma200.color} on={chartSettings.movingAverages.ma200} onClick={() => toggleMovingAverage("ma200")} />
            <div className="field inline-field"><span>Custom MA</span><input type="text" inputMode="numeric" value={customMaInput} onChange={(event) => setCustomMovingAverage(event.target.value)} onBlur={commitCustomMovingAverage} onKeyDown={(event) => { if (event.key === "Enter") event.currentTarget.blur(); }} /></div>
            <Check label={`MA ${customMaInput.trim() || chartSettings.movingAverages.customPeriod}`} color={chartSettings.styles.customMa.color} on={chartSettings.movingAverages.custom} onClick={() => toggleMovingAverage("custom")} />
          </OptionCard>
          <OptionCard title="Chart Overlays">
            <Check label="Prior High Line" color={chartSettings.styles.priorHigh.color} on={chartSettings.overlays.priorHigh} onClick={() => toggleOverlay("priorHigh")} />
            <Check label="CP Ladder Overlay" color={chartSettings.styles.ladder.color} on={chartSettings.overlays.cpLadder} onClick={() => toggleOverlay("cpLadder")} />
            <Check label="Session Background" on={chartSettings.overlays.sessionBackground} onClick={() => toggleOverlay("sessionBackground")} />
            <Check label="Futures Basis Ribbon" color={chartSettings.styles.futuresBasis.color} on={chartSettings.overlays.futuresBasis} onClick={() => toggleOverlay("futuresBasis")} />
          </OptionCard>
          <OptionCard title="Signal Integrity">
            <div className="signal-mini"><b className="amber">Data Freshness</b><span>{chartData?.quote?.freshness ?? "cached"}</span></div>
            <div className="signal-mini"><b className="green">Source</b><span>{chartData?.quote?.source ?? selectedInstrument.primarySource}</span></div>
            <div className="signal-mini"><b className="blue">OHLC Source</b><span>{latestChartBar?.source ?? "none"}</span></div>
            <div className="signal-mini"><b className="red">Provider Risk</b><span>{chartData?.quote?.riskLevel ?? "medium"}</span></div>
          </OptionCard>
        </div>
        <div className="section chart-palette-section">
          <button className="section-subhead palette-toggle" type="button" onClick={() => setPaletteOpen((value) => !value)} aria-expanded={paletteOpen}>
            <span>Line Style Palette</span>
            <span className="subhead-status">{chartSettingsStatus} {paletteOpen ? <ChevronUp /> : <ChevronDown />}</span>
          </button>
          {paletteOpen && (
            <div className="palette-grid">
              {paletteItems.map((item) => (
                <LineStyleControl
                  key={item.key}
                  label={item.label}
                  style={chartSettings.styles[item.key]}
                  onChange={(patch) => updateLineStyle(item.key, patch)}
                />
              ))}
            </div>
          )}
        </div>
      </Panel>}

      {allocationEditor && (
        <AllocationEditorModal
          currency={budgetCurrency}
          editor={allocationEditor}
          onChange={setAllocationEditor}
          onClose={() => setAllocationEditor(null)}
          onSave={saveAllocationEditor}
          step={steps.find((step) => step.id === allocationEditor.stepId)}
        />
      )}
    </section>
  );
}

function Panel({ id, letter, title, subtitle, children, fullscreen, setFullscreen }: { id: string; letter: string; title: string; subtitle: string; children: ReactNode; fullscreen: string | null; setFullscreen: (id: string | null) => void }) {
  const isExpanded = fullscreen === id;
  return (
    <article className={`column ${isExpanded ? "is-expanded" : ""} ${fullscreen && !isExpanded ? "is-hidden" : ""}`}>
      <div className="panel-head">
        <div className="panel-letter">{letter}</div>
        <div className="panel-title"><h2>{title}</h2><p>{subtitle}</p></div>
        <button className="panel-icon" onClick={() => setFullscreen(isExpanded ? null : id)} title={isExpanded ? "Exit fullscreen" : "Expand panel"} aria-label={isExpanded ? "Exit fullscreen" : "Expand panel"}>
          <Maximize2 />
        </button>
      </div>
      {children}
    </article>
  );
}

function ChartSkeleton({ label }: { label: string }) {
  return (
    <div className="chart-skeleton" role="status" aria-label={label}>
      <div className="chart-skeleton-legend">
        <span />
        <span />
        <span />
      </div>
      <div className="chart-skeleton-plot">
        <i className="rise one" />
        <i className="rise two" />
        <i className="drop" />
      </div>
      <b>{label}</b>
    </div>
  );
}

function Summary({ label, value, amber }: { label: string; value: string; amber?: boolean }) {
  return <div className="summary-cell"><span className="mini-label">{label}</span><b className={amber ? "amber" : ""}>{value}</b></div>;
}

function Stat({ label, value, subValue }: { label: string; value: string; subValue?: string }) {
  return (
    <div className="stat-cell">
      <span className="mini-label">{label}</span>
      <span className="stat-value">{value}</span>
      {subValue && <span className="stat-subvalue">{subValue}</span>}
    </div>
  );
}

function AllocationEditorModal({
  currency,
  editor,
  onChange,
  onClose,
  onSave,
  step
}: {
  currency: BudgetCurrency;
  editor: AllocationEditorState;
  onChange: (next: AllocationEditorState) => void;
  onClose: () => void;
  onSave: () => void;
  step?: { label: string; budget: number; indexLevel: number };
}) {
  const amount = editableNumber(editor.amountText, { min: 0 }) ?? 0;
  const pctOfStep = step?.budget ? (amount / step.budget) * 100 : 0;

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Allocation editor">
      <div className="allocation-modal">
        <div className="modal-head">
          <div>
            <h3>{editor.mode === "edit" ? "Edit Allocation" : "Add Allocation"}</h3>
            <span>{step ? `${step.label} / ${number(step.indexLevel)} / ${money(step.budget, currency)} budget` : "Step not found"}</span>
          </div>
          <button className="mini-btn" type="button" onClick={onClose}>Close</button>
        </div>
        <div className="allocation-modal-grid">
          <label className="field">
            <span>Ticker / Label</span>
            <input
              autoFocus
              type="text"
              value={editor.assetTicker}
              onChange={(event) => onChange({ ...editor, assetTicker: event.target.value })}
              placeholder="SPY, QQQ, Cash..."
            />
          </label>
          <label className="field">
            <span>Asset Name</span>
            <input
              type="text"
              value={editor.assetName}
              onChange={(event) => onChange({ ...editor, assetName: event.target.value })}
              placeholder="Optional display name"
            />
          </label>
          <label className="field">
            <span>Amount</span>
            <input
              inputMode="decimal"
              type="text"
              value={editor.amountText}
              onChange={(event) => onChange({ ...editor, amountText: event.target.value })}
            />
          </label>
        </div>
        <div className="allocation-color-block">
          <span>Label Color</span>
          <div className="allocation-color-grid">
            {allocationPaletteSwatches.map((swatch) => (
              <button
                aria-label={`Use ${swatch} label color`}
                className={`allocation-color-swatch ${editor.labelColor === swatch ? "is-selected" : ""}`}
                key={swatch}
                onClick={() => onChange({ ...editor, labelColor: swatch })}
                style={{ backgroundColor: swatch }}
                type="button"
              />
            ))}
          </div>
        </div>
        <div className="allocation-modal-summary">
          <span>Auto % of this step</span>
          <b>{pctOfStep.toFixed(1)}%</b>
          <em>{money(amount, currency)}</em>
        </div>
        <div className="modal-actions">
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button className="btn primary" type="button" onClick={onSave}>{editor.mode === "edit" ? "Save Changes" : "Add Label"}</button>
        </div>
      </div>
    </div>
  );
}

function ChartSettingsPanel({
  settings,
  status,
  onChartChange,
  onToggleOverlay,
  onToggleMovingAverage,
  onReset
}: {
  settings: ChartSettings;
  status: string;
  onChartChange: (patch: Partial<ChartSettings["chart"]>) => void;
  onToggleOverlay: (key: keyof ChartSettings["overlays"]) => void;
  onToggleMovingAverage: (key: MovingAverageKey) => void;
  onReset: () => void;
}) {
  return (
    <div className="section chart-settings-panel">
      <div className="section-subhead">Chart Settings <span className="subhead-status">{status}</span></div>
      <div className="chart-settings-grid">
        <div className="settings-block">
          <div className="section-title">Display</div>
          <label className="field"><span>Range</span><select value={settings.chart.range} onChange={(event) => onChartChange({ range: event.target.value as ChartSettings["chart"]["range"] })}><option value="3M">3M</option><option value="6M">6M</option><option value="1Y">1Y</option><option value="3Y">3Y</option><option value="10Y">10Y</option></select></label>
          <label className="field"><span>View</span><select value={settings.chart.view} onChange={(event) => onChartChange({ view: event.target.value as ChartSettings["chart"]["view"] })}><option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option></select></label>
          <label className="field"><span>Chart Height</span><input type="number" min="260" max="780" value={settings.chart.height} onChange={(event) => onChartChange({ height: cleanNumber(event.target.value, settings.chart.height, { min: 260, max: 780, integer: true }) })} /></label>
          <label className="field"><span>Density</span><select value={settings.chart.density} onChange={(event) => onChartChange({ density: event.target.value as ChartSettings["chart"]["density"] })}><option value="compact">Compact</option><option value="standard">Standard</option><option value="wide">Wide</option></select></label>
        </div>
        <div className="settings-block">
          <div className="section-title">Chart Behavior</div>
          <Check label="Legend" on={settings.chart.showLegend} onClick={() => onChartChange({ showLegend: !settings.chart.showLegend })} />
          <Check label="Tooltip" on={settings.chart.showTooltip} onClick={() => onChartChange({ showTooltip: !settings.chart.showTooltip })} />
          <Check label="Data Zoom Slider" on={settings.chart.showDataZoom} onClick={() => onChartChange({ showDataZoom: !settings.chart.showDataZoom })} />
          <label className="field"><span>Candle Color</span><select value={settings.chart.candleColorMode} onChange={(event) => onChartChange({ candleColorMode: event.target.value as ChartSettings["chart"]["candleColorMode"] })}><option value="kr">Korea: Red Up / Blue Down</option><option value="us">US: Green Up / Red Down</option></select></label>
        </div>
        <div className="settings-block">
          <div className="section-title">Active Layers</div>
          <Check label="Prior High Line" color={settings.styles.priorHigh.color} on={settings.overlays.priorHigh} onClick={() => onToggleOverlay("priorHigh")} />
          <Check label="CP Ladder Overlay" color={settings.styles.ladder.color} on={settings.overlays.cpLadder} onClick={() => onToggleOverlay("cpLadder")} />
          <Check label="Session Background" on={settings.overlays.sessionBackground} onClick={() => onToggleOverlay("sessionBackground")} />
          <Check label="Futures Basis Ribbon" color={settings.styles.futuresBasis.color} on={settings.overlays.futuresBasis} onClick={() => onToggleOverlay("futuresBasis")} />
        </div>
        <div className="settings-block">
          <div className="section-title">Indicators</div>
          <Check label="MA 20" color={settings.styles.ma20.color} on={settings.movingAverages.ma20} onClick={() => onToggleMovingAverage("ma20")} />
          <Check label="MA 50" color={settings.styles.ma50.color} on={settings.movingAverages.ma50} onClick={() => onToggleMovingAverage("ma50")} />
          <Check label="MA 200" color={settings.styles.ma200.color} on={settings.movingAverages.ma200} onClick={() => onToggleMovingAverage("ma200")} />
          <button className="btn full-btn" type="button" onClick={onReset}><RotateCcw /> Reset Chart Settings</button>
        </div>
      </div>
    </div>
  );
}

function OptionCard({ title, children }: { title: string; children: ReactNode }) {
  return <div className="option-card"><div className="section-title">{title}</div>{children}</div>;
}

function Check({ label, color, on, disabled, onClick }: { label: string; color?: string; on?: boolean; disabled?: boolean; onClick?: () => void }) {
  return (
    <button className={`check-row ${disabled ? "is-disabled" : ""}`} disabled={disabled} onClick={onClick} title={disabled ? "No provider-backed data for this metric and instrument" : undefined} type="button">
      <span className={`box ${on && !disabled ? "on" : ""}`} />
      <span>{label}</span>
      {disabled ? <span className="check-na">N/A</span> : <span className="color-dot" style={{ background: color ?? "transparent" }} />}
    </button>
  );
}

function LineStyleControl({ label, style, onChange }: { label: string; style: ChartLineStyleConfig; onChange: (patch: Partial<ChartLineStyleConfig>) => void }) {
  return (
    <div className="palette-row">
      <div className="palette-name">
        <span className="palette-swatch" style={{ background: style.color }} />
        <b>{label}</b>
      </div>
      <div className="palette-color-grid" role="radiogroup" aria-label={`${label} color`}>
        {chartPaletteSwatches.map((swatch) => (
          <button
            aria-label={`${label} ${swatch}`}
            aria-checked={style.color.toLowerCase() === swatch.toLowerCase()}
            className={`palette-color-swatch ${style.color.toLowerCase() === swatch.toLowerCase() ? "is-selected" : ""}`}
            key={swatch}
            onClick={() => onChange({ color: swatch })}
            style={{ background: swatch }}
            type="button"
          >
            <span className="sr-only">{swatch}</span>
          </button>
        ))}
      </div>
      <label className="palette-field">
        <span>Width</span>
        <input aria-label={`${label} width`} type="number" min="0.5" max="6" step="0.1" value={style.width} onChange={(event) => onChange({ width: cleanNumber(event.target.value, style.width, { min: 0.5, max: 6 }) })} />
      </label>
      <label className="palette-field">
        <span>Type</span>
        <select aria-label={`${label} line type`} value={style.type} onChange={(event) => onChange({ type: event.target.value as ChartLineStyleConfig["type"] })}>
          <option value="solid">Solid</option>
          <option value="dashed">Dashed</option>
          <option value="dotted">Dotted</option>
        </select>
      </label>
    </div>
  );
}
